import { useCallback, useEffect, useRef, useState } from "react";

const IS_SERVER = typeof window === "undefined";

export interface UseSnackbarOptions {
	/** Key used in sessionStorage to enforce once-per-session display. */
	sessionKey: string;
	/** Auto-dismiss after this many milliseconds. No auto-dismiss if omitted. */
	autoDismissMs?: number;
	/** When true, the snackbar can be shown multiple times per session. */
	isRetriggerable?: boolean;
	/** Called when the auto-dismiss timer hides the snackbar. Not called on manual dismiss. */
	onAutoDismiss?: () => void;
}

export interface UseSnackbarReturn {
	isVisible: boolean;
	/** Show the snackbar. Returns true if shown, false if suppressed by session guard. */
	show: () => boolean;
	dismiss: () => void;
	/** Pause auto-dismiss timer (e.g. on hover). */
	pauseAutoDismiss: () => void;
	/** Resume auto-dismiss timer with remaining time (e.g. on hover leave). */
	resumeAutoDismiss: () => void;
}

function hasBeenShown(key: string): boolean {
	if (IS_SERVER) return true;
	try {
		return sessionStorage.getItem(key) === "true";
	} catch {
		return false;
	}
}

function markAsShown(key: string): void {
	if (IS_SERVER) return;
	try {
		sessionStorage.setItem(key, "true");
	} catch {
		/* storage unavailable (e.g. Safari private browsing) */
	}
}

/** Manages snackbar visibility with optional once-per-session guardrail and hover-pausable auto-dismiss. */
export function useSnackbar({
	sessionKey,
	autoDismissMs,
	isRetriggerable = false,
	onAutoDismiss,
}: UseSnackbarOptions): UseSnackbarReturn {
	const [isVisible, setIsVisible] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const remainingMsRef = useRef<number | null>(null);
	const timerStartedAtRef = useRef<number | null>(null);
	const onAutoDismissRef = useRef(onAutoDismiss);
	useEffect(() => {
		onAutoDismissRef.current = onAutoDismiss;
	}, [onAutoDismiss]);

	const clearTimer = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		timerStartedAtRef.current = null;
	}, []);

	const startTimer = useCallback(
		(durationMs?: number) => {
			const duration = durationMs ?? autoDismissMs;
			if (!duration) return;
			clearTimer();
			remainingMsRef.current = duration;
			timerStartedAtRef.current = Date.now();
			timerRef.current = setTimeout(() => {
				setIsVisible(false);
				remainingMsRef.current = null;
				onAutoDismissRef.current?.();
			}, duration);
		},
		[autoDismissMs, clearTimer],
	);

	const pauseAutoDismiss = useCallback(() => {
		if (!timerRef.current || timerStartedAtRef.current === null) return;
		const elapsed = Date.now() - timerStartedAtRef.current;
		const remaining = (remainingMsRef.current ?? 0) - elapsed;
		remainingMsRef.current = Math.max(remaining, 0);
		clearTimer();
	}, [clearTimer]);

	const resumeAutoDismiss = useCallback(() => {
		if (remainingMsRef.current === null || remainingMsRef.current <= 0) return;
		startTimer(remainingMsRef.current);
	}, [startTimer]);

	const show = useCallback(() => {
		if (!isRetriggerable && hasBeenShown(sessionKey)) return false;
		if (!isRetriggerable) {
			markAsShown(sessionKey);
		}
		setIsVisible(true);
		startTimer();
		return true;
	}, [sessionKey, startTimer, isRetriggerable]);

	const dismiss = useCallback(() => {
		clearTimer();
		remainingMsRef.current = null;
		setIsVisible(false);
	}, [clearTimer]);

	useEffect(() => {
		return clearTimer;
	}, [clearTimer]);

	return { isVisible, show, dismiss, pauseAutoDismiss, resumeAutoDismiss };
}
